import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function ProjectManagementPage() {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/echarts@5.4.3/dist/echarts.min.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      initChart();
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const initChart = () => {
    const chartDom = document.getElementById('performance-chart');
    if (chartDom && (window as any).echarts) {
      const myChart = (window as any).echarts.init(chartDom);
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: ['Target', 'Actual']
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          max: 100
        },
        yAxis: {
          type: 'category',
          data: ['On-Time\nDelivery', 'Within\nBudget', 'Client\nSatisfaction', 'Scope\nAdherence']
        },
        series: [
          {
            name: 'Target',
            type: 'bar',
            data: [85, 80, 90, 85],
            itemStyle: {
              color: '#E5E7EB'
            }
          },
          {
            name: 'Actual',
            type: 'bar',
            data: [94, 91, 97, 89],
            itemStyle: {
              color: '#2563EB'
            }
          }
        ]
      };
      myChart.setOption(option);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    const data: any = {};
    formData.forEach((value, key) => {
      data[key] = value;
    });

    fetch('https://readdy.ai/api/public/form/submit/project-management-consultation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(data).toString()
    })
      .then(() => {
        setShowSuccessModal(true);
        form.reset();
        setCharCount(0);
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCharCount(e.target.value.length);
  };

  const scrollToForm = () => {
    const formSection = document.getElementById('consultation-form-section');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative w-full bg-gradient-to-r from-blue-50 to-white">
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-transparent"></div>
        <div className="container mx-auto px-4 py-20 relative">
          <div className="w-full max-w-2xl">
            <a
              href="/"
              className="inline-flex items-center text-primary mb-4 hover:underline cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-arrow-left-line"></i>
              </div>
              Back to Original Page
            </a>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Project Management Services
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              Expert guidance to plan, execute, and deliver your critical IT initiatives on time, within budget, and to specification, ensuring maximum business value.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={scrollToForm}
                className="bg-primary text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium cursor-pointer hover:bg-primary/90 transition-colors"
              >
                Schedule Consultation
              </button>
              <a
                href="#services"
                className="bg-white border border-gray-300 text-gray-800 px-6 py-3 rounded-lg whitespace-nowrap font-medium cursor-pointer inline-block text-center hover:bg-gray-50 transition-colors"
              >
                View Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Services Section */}
      <section id="services" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold mb-4">Comprehensive Project Management Services</h2>
            <p className="text-gray-600">
              Our certified project management professionals deliver structured, methodical approaches to ensure your IT initiatives succeed from inception to completion.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6 transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100 cursor-pointer">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-file-chart-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Project Planning</h3>
              <p className="text-gray-600 mb-4">
                Comprehensive project scoping, requirements gathering, resource allocation, and timeline development to establish a solid foundation.
              </p>
              <a href="#" className="text-primary font-medium flex items-center cursor-pointer">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100 cursor-pointer">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-team-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Project Execution</h3>
              <p className="text-gray-600 mb-4">
                Expert coordination of project activities, resource management, stakeholder communication, and quality control throughout implementation.
              </p>
              <a href="#" className="text-primary font-medium flex items-center cursor-pointer">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100 cursor-pointer">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-line-chart-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Monitoring & Control</h3>
              <p className="text-gray-600 mb-4">
                Rigorous tracking of project progress, performance metrics, risk management, and change control to ensure alignment with objectives.
              </p>
              <a href="#" className="text-primary font-medium flex items-center cursor-pointer">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100 cursor-pointer">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-flag-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Project Closure</h3>
              <p className="text-gray-600 mb-4">
                Systematic project completion, deliverable validation, knowledge transfer, documentation, and post-implementation review for continuous improvement.
              </p>
              <a href="#" className="text-primary font-medium flex items-center cursor-pointer">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-4">Our Project Management Methodology</h2>
            <p className="text-gray-600">
              A structured, proven approach to managing IT projects that ensures consistent delivery of high-quality results while minimizing risks.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            <div className="relative process-step">
              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100 h-full">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 relative z-10">
                  <span className="text-primary text-2xl font-bold">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Initiation</h3>
                <p className="text-gray-600 mb-4">
                  We define project scope, objectives, and stakeholders to establish a clear vision and foundation.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Project charter</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Stakeholder analysis</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Feasibility assessment</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative process-step">
              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100 h-full">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 relative z-10">
                  <span className="text-primary text-2xl font-bold">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Planning</h3>
                <p className="text-gray-600 mb-4">
                  We develop detailed project plans, schedules, and resource requirements to guide execution.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Work breakdown structure</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Resource allocation</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Risk management plan</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative process-step">
              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100 h-full">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 relative z-10">
                  <span className="text-primary text-2xl font-bold">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Execution</h3>
                <p className="text-gray-600 mb-4">
                  We coordinate resources and activities to implement the project plan and deliver results.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Team coordination</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Quality assurance</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Stakeholder communication</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative process-step">
              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100 h-full">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 relative z-10">
                  <span className="text-primary text-2xl font-bold">4</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Monitoring</h3>
                <p className="text-gray-600 mb-4">
                  We track progress, manage changes, and address issues to keep the project on track.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Performance tracking</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Change control</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Risk mitigation</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative process-step">
              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100 h-full">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 relative z-10">
                  <span className="text-primary text-2xl font-bold">5</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Closure</h3>
                <p className="text-gray-600 mb-4">
                  We formalize project completion, document lessons learned, and ensure knowledge transfer.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Deliverable acceptance</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Documentation</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Post-implementation review</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-4">Why Choose Our Project Management Services</h2>
            <p className="text-gray-600">
              Experience the difference that professional project management brings to your IT initiatives, delivering measurable business value.
            </p>
          </div>

          {/* Structured Approach */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div className="order-2 lg:order-1">
              <span className="text-primary font-semibold mb-2 block">STRUCTURED APPROACH</span>
              <h3 className="text-2xl font-bold mb-4">Predictable, Consistent Results</h3>
              <p className="text-gray-600 mb-6">
                Our structured project management methodology ensures consistent delivery of high-quality results across all your IT initiatives, minimizing risks and maximizing business value.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-check-double-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Standardized Processes</h4>
                    <p className="text-gray-600">
                      Proven methodologies and templates that ensure consistency and quality across all project phases.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-shield-check-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Risk Mitigation</h4>
                    <p className="text-gray-600">
                      Proactive identification and management of potential issues before they impact project outcomes.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-calendar-check-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">On-Time Delivery</h4>
                    <p className="text-gray-600">
                      Rigorous scheduling and milestone tracking to ensure projects are completed within established timeframes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100">
                <img
                  src="https://public.readdy.ai/ai/img_res/9dc14827e948019a7870f3f2dc8f5c88.jpg"
                  alt="Project Management Team"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Stakeholder Management */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Project%20manager%20leading%20a%20stakeholder%20meeting%20with%20executives%20and%20team%20members.%20The%20image%20shows%20a%20professional%20presentation%20of%20project%20status%20with%20performance%20metrics%20and%20KPIs%20on%20display.%20The%20setting%20is%20a%20modern%20conference%20room%20with%20digital%20displays%20showing%20dashboards%20and%20project%20analytics.%20The%20atmosphere%20is%20collaborative%20and%20focused%20on%20strategic%20decision-making.&width=600&height=400&seq=pm2&orientation=landscape"
                  alt="Stakeholder Management"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
            <div>
              <span className="text-primary font-semibold mb-2 block">STAKEHOLDER MANAGEMENT</span>
              <h3 className="text-2xl font-bold mb-4">Transparent Communication</h3>
              <p className="text-gray-600 mb-6">
                We ensure all stakeholders are aligned and informed throughout the project lifecycle, fostering collaboration and preventing misalignment of expectations.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-presentation-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Regular Reporting</h4>
                    <p className="text-gray-600">
                      Comprehensive status updates and performance metrics that keep all stakeholders informed of progress.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-team-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Stakeholder Engagement</h4>
                    <p className="text-gray-600">
                      Strategic involvement of key stakeholders at critical decision points to ensure alignment with business objectives.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-chat-check-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Expectation Management</h4>
                    <p className="text-gray-600">
                      Clear communication of project scope, constraints, and deliverables to prevent scope creep and ensure satisfaction.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Resource Optimization */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="order-2 lg:order-1">
              <span className="text-primary font-semibold mb-2 block">RESOURCE OPTIMIZATION</span>
              <h3 className="text-2xl font-bold mb-4">Maximized Efficiency</h3>
              <p className="text-gray-600 mb-6">
                Our project managers ensure optimal allocation and utilization of resources, minimizing waste and maximizing return on investment for your IT initiatives.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-funds-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Budget Management</h4>
                    <p className="text-gray-600">
                      Careful tracking and control of project expenses to ensure delivery within approved budgets.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-user-settings-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Resource Allocation</h4>
                    <p className="text-gray-600">
                      Strategic assignment of the right resources to the right tasks at the right time to maximize productivity.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-time-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Time Management</h4>
                    <p className="text-gray-600">
                      Efficient scheduling and task management to minimize downtime and accelerate project completion.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Project%20resource%20management%20visualization%20showing%20resource%20allocation%20and%20utilization%20charts.%20The%20image%20displays%20professional%20project%20management%20software%20with%20resource%20histograms%2C%20capacity%20planning%20tools%2C%20and%20budget%20tracking%20interfaces.%20The%20visualization%20includes%20team%20workload%20balancing%20and%20skill%20allocation%20metrics%20in%20a%20clean%2C%20data-driven%20dashboard%20format.&width=600&height=400&seq=pm3&orientation=landscape"
                  alt="Resource Optimization"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Measurable Results Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2">
              <span className="text-primary font-semibold mb-2 block">MEASURABLE RESULTS</span>
              <h2 className="text-3xl font-bold mb-6">Driving Project Success</h2>
              <p className="text-gray-600 mb-6">
                Our project management services consistently deliver measurable improvements in project outcomes across key performance indicators.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-timer-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">On-Time Delivery</h3>
                    <p className="text-gray-600">
                      94% of our managed projects are delivered on or ahead of schedule, compared to the industry average of 64%.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-money-dollar-circle-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Budget Adherence</h3>
                    <p className="text-gray-600">
                      91% of projects completed within or under budget, delivering an average cost savings of 12% through efficient resource management.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-customer-service-2-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Client Satisfaction</h3>
                    <p className="text-gray-600">
                      97% client satisfaction rate, with 85% of clients engaging our services for additional projects.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
                <h3 className="text-xl font-semibold mb-4 text-center">Project Performance Metrics</h3>
                <div id="performance-chart" className="w-full h-80"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA and Form Section */}
      <section id="consultation-form-section" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 lg:p-12 bg-primary text-white">
                  <h2 className="text-3xl font-bold mb-6">Ready to Ensure Your Project Success?</h2>
                  <p className="mb-8">
                    Schedule a consultation with our project management experts to discuss your specific needs and how we can help you achieve your business objectives.
                  </p>
                  <div className="space-y-6 mb-8">
                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 mt-1">
                        <div className="w-6 h-6 flex items-center justify-center text-white">
                          <i className="ri-check-line"></i>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">Expert Assessment</h3>
                        <p className="text-blue-100">
                          Comprehensive evaluation of your project needs and objectives by certified professionals.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 mt-1">
                        <div className="w-6 h-6 flex items-center justify-center text-white">
                          <i className="ri-check-line"></i>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">Tailored Approach</h3>
                        <p className="text-blue-100">
                          Customized project management solutions designed for your specific industry and requirements.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 mt-1">
                        <div className="w-6 h-6 flex items-center justify-center text-white">
                          <i className="ri-check-line"></i>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">Flexible Engagement Models</h3>
                        <p className="text-blue-100">
                          From full project management to advisory services, we adapt to your organizational needs.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-8 lg:p-12">
                  <h3 className="text-2xl font-bold mb-6 text-gray-900">Request a Project Management Consultation</h3>
                  <form data-readdy-form id="project-management-consultation-form" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                          Name
                        </label>
                        <input
                          id="name"
                          name="name"
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Your name"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
                          Company
                        </label>
                        <input
                          id="company"
                          name="company"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Your company"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Your email"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Your phone number"
                          required
                        />
                      </div>
                    </div>
                    <div className="mb-6">
                      <label htmlFor="project" className="block text-gray-700 font-medium mb-2">
                        Project Description
                      </label>
                      <textarea
                        id="project"
                        name="project"
                        rows={4}
                        maxLength={500}
                        onChange={handleTextareaChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Briefly describe your project needs and objectives"
                        required
                      ></textarea>
                      <div className="text-sm text-gray-500 mt-1">
                        <span id="char-count">{charCount}</span>/500 characters
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">Project Type</label>
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="project_type"
                              value="new"
                              className="w-5 h-5 text-primary border-gray-300 focus:ring-primary"
                              required
                            />
                            <span className="ml-2">New Project</span>
                          </label>
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="project_type"
                              value="existing"
                              className="w-5 h-5 text-primary border-gray-300 focus:ring-primary"
                            />
                            <span className="ml-2">Existing Project</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="budget" className="block text-gray-700 font-medium mb-2">
                          Budget Range
                        </label>
                        <div className="relative">
                          <select
                            id="budget"
                            name="budget"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-8 appearance-none cursor-pointer"
                            required
                          >
                            <option value="">Select budget range</option>
                            <option value="under50k">Under $50,000</option>
                            <option value="50k-100k">$50,000 - $100,000</option>
                            <option value="100k-250k">$100,000 - $250,000</option>
                            <option value="over250k">Over $250,000</option>
                          </select>
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mb-6">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="terms_accepted"
                          className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary"
                          required
                        />
                        <span className="ml-2 text-sm text-gray-600">
                          I agree to Thyamis's privacy policy and terms of service
                        </span>
                      </label>
                    </div>
                    <button
                      type="submit"
                      className="bg-primary text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium w-full cursor-pointer hover:bg-primary/90 transition-colors"
                    >
                      Submit Request
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-check-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-2">Request Submitted!</h3>
              <p className="text-gray-600 mb-6">
                Thank you for your interest. Our project management team will contact you within 24 hours to discuss your needs.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="bg-primary text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium cursor-pointer hover:bg-primary/90 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
